'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { EventSource } from 'eventsource';
import {
  PhonicsLesson,
  PhonicsLiveLessonMembershipStatus,
  PhonicsLiveLessonOnboardingStatus,
  PhonicsLiveLessonStatus,
  PicturePasswordItemFragment,
  useCreatePhonicsLiveLessonMembershipMutation,
  useCreatePhonicsLiveLessonMutation,
  useCreatePhonicsLoginMutation,
  useGetPhonicsLessonQuery,
  useGetPhonicsLiveLessonMembershipStatusLazyQuery,
  useGetPhonicsLiveLessonOnboardingStatusLazyQuery,
  useGetPhonicsLiveLessonStatusLazyQuery,
} from '@/api/types';
import { useAuth } from '@/context/Authentication';
import { useData } from '@/context/Data';

interface LiveLessonContextData {
  liveLesson?: PhonicsLesson;
  setLiveLesson: (lesson?: PhonicsLesson) => void;

  tryFetchLiveLessonStatus: () => void;
  liveLessonStatus?: PhonicsLiveLessonStatus;
  setLiveLessonStatus?: (status?: PhonicsLiveLessonStatus) => void;
  liveLessonStatusError?: string;
  beginLiveLesson: () => void;
  beginLiveLessonLoading?: boolean;
  beginLiveLessonError?: string;
  endLiveLesson: () => void;

  requestLiveLessonOnboardingStatus: (accessCode: string) => void;
  liveLessonOnboardingStatus?: PhonicsLiveLessonOnboardingStatus;
  liveLessonOnboardingStatusLoading: boolean;
  liveLessonOnboardingStatusError?: string;

  liveLessonLogin: (
    accessCode: string,
    classId: string,
    userId: string,
    passCode?: Array<PicturePasswordItemFragment>,
    password?: string,
  ) => void;
  liveLessonLoginLoading: boolean;
  liveLessonLoginError?: string;

  joinLiveLesson: (accessCode: string, classId: string) => void;
  tryFetchLiveLessonMembershipStatus: () => void;
  liveLessonMembershipStatus?: PhonicsLiveLessonMembershipStatus;
  liveLessonMembershipLoading: boolean;
  liveLessonMembershipError?: string;
}

const initialValue: LiveLessonContextData = {
  liveLesson: undefined,
  setLiveLesson: () => undefined,
  tryFetchLiveLessonStatus: () => undefined,
  liveLessonStatus: undefined,
  setLiveLessonStatus: () => undefined,
  liveLessonStatusError: undefined,
  beginLiveLesson: () => undefined,
  beginLiveLessonLoading: false,
  beginLiveLessonError: undefined,
  endLiveLesson: () => undefined,
  requestLiveLessonOnboardingStatus: () => undefined,
  liveLessonOnboardingStatus: undefined,
  liveLessonOnboardingStatusLoading: false,
  liveLessonOnboardingStatusError: undefined,
  liveLessonLogin: () => undefined,
  liveLessonLoginLoading: false,
  liveLessonLoginError: undefined,
  joinLiveLesson: () => undefined,
  tryFetchLiveLessonMembershipStatus: () => undefined,
  liveLessonMembershipStatus: undefined,
  liveLessonMembershipLoading: false,
  liveLessonMembershipError: undefined,
};

const LiveLessonContext = createContext<LiveLessonContextData>(initialValue);

export const LiveLessonContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { classStatus } = useData();

  const { token, storeToken } = useAuth();

  const [liveLesson, setLiveLesson] = useState<PhonicsLesson | undefined>();

  const [readyForLiveLesson, setReadyForLiveLesson] = useState(false);

  const [liveLessonStatus, setLiveLessonStatus] = useState<
    PhonicsLiveLessonStatus | undefined
  >(undefined);

  const [liveLessonStatusError, setLiveLessonStatusError] = useState<
    string | undefined
  >();

  const [getLiveLessonStatus] = useGetPhonicsLiveLessonStatusLazyQuery({
    onCompleted: (data) => {
      setLiveLessonStatus(data.getPhonicsLiveLessonStatus || undefined);
    },
  });

  const [
    createPhonicsLiveLesson,
    {
      loading: createPhonicsLiveLessonLoading,
      error: createPhonicsLiveLessonError,
    },
  ] = useCreatePhonicsLiveLessonMutation();

  const [liveLessonOnboardingStatus, setLiveLessonOnboardingStatus] = useState<
    PhonicsLiveLessonOnboardingStatus | undefined
  >();

  const [
    getPhonicsLiveLessonOnboardingStatus,
    {
      data: liveLessonOnboardingStatusResult,
      loading: liveLessonOnboardingStatusLoading,
      error: liveLessonOnboardingStatusError,
    },
  ] = useGetPhonicsLiveLessonOnboardingStatusLazyQuery();

  const [
    createPhonicsLogin,
    { loading: phonicsLoginLoading, error: phonicsLoginError },
  ] = useCreatePhonicsLoginMutation({
    onCompleted: (data) => {
      storeToken(data.createPhonicsLogin.token);
    },
  });

  const [
    getPhonicsLiveLessonMembershipStatus,
    { data: liveLessonMembershipStatusResult },
  ] = useGetPhonicsLiveLessonMembershipStatusLazyQuery();

  const [liveLessonMembershipStatus, setLiveLessonMembershipStatus] = useState<
    PhonicsLiveLessonMembershipStatus | undefined
  >();

  const [
    createPhonicsLiveLessonMembership,
    {
      data: liveLessonMembershipResult,
      loading: liveLessonMembershipLoading,
      error: liveLessonMembershipError,
    },
  ] = useCreatePhonicsLiveLessonMembershipMutation();

  const { data: liveLessonResult } = useGetPhonicsLessonQuery({
    variables: {
      id:
        liveLessonMembershipResult?.createPhonicsLiveLessonMembership
          .lessonId || '',
    },
    skip: !liveLessonMembershipResult?.createPhonicsLiveLessonMembership,
  });

  const tryFetchLiveLessonStatus = useCallback(() => {
    if (!classStatus) {
      return;
    }
    if (!liveLesson) {
      return;
    }

    getLiveLessonStatus({
      variables: {
        input: {
          classId: classStatus.classId,
          lessonId: liveLesson.id,
        },
      },
    });
  }, [classStatus, liveLesson, getLiveLessonStatus]);

  const beginLiveLesson = useCallback(() => setReadyForLiveLesson(true), []);

  const endLiveLesson = useCallback(() => {
    setLiveLessonStatus(undefined);
    setLiveLesson(undefined);
    setReadyForLiveLesson(false);
  }, []);

  const handleLiveLessonStatusUpdate = (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);

      setLiveLessonStatus(
        Object.keys(data).length === 0 || [undefined, null].includes(data)
          ? undefined
          : data,
      );
    } catch (error) {
      console.error('Error while updating live lesson status', error);
    }
  };

  const liveLessonLogin = useCallback(
    (
      accessCode: string,
      classId: string,
      userId: string,
      passcode?: Array<PicturePasswordItemFragment>,
      password?: string,
    ) =>
      createPhonicsLogin({
        variables: {
          input: {
            accessCode,
            classId,
            userId,
            passCode: passcode?.map((it) => it.value),
            password,
          },
        },
      }),
    [createPhonicsLogin],
  );

  const joinLiveLesson = useCallback(
    (accessCode: string, classId: string) =>
      createPhonicsLiveLessonMembership({
        variables: {
          input: {
            accessCode,
            classId,
          },
        },
      }),
    [createPhonicsLiveLessonMembership],
  );

  const requestLiveLessonOnboardingStatus = useCallback(
    (accessCode: string) =>
      getPhonicsLiveLessonOnboardingStatus({
        variables: {
          accessCode,
        },
      }),
    [getPhonicsLiveLessonOnboardingStatus],
  );

  const tryFetchLiveLessonMembershipStatus = useCallback(() => {
    if (!liveLesson) {
      return;
    }

    getPhonicsLiveLessonMembershipStatus({
      variables: {
        input: {
          lessonId: liveLesson?.id,
        },
      },
    });
  }, [getPhonicsLiveLessonMembershipStatus, liveLesson]);

  useEffect(() => {
    if (!readyForLiveLesson) {
      return;
    }
    if (!classStatus) {
      return;
    }
    if (!liveLesson) {
      return;
    }

    createPhonicsLiveLesson({
      variables: {
        input: {
          classId: classStatus.classId,
          lessonId: liveLesson.id,
        },
      },
    });
  }, [readyForLiveLesson, classStatus, liveLesson, createPhonicsLiveLesson]);

  useEffect(() => {
    if (!liveLessonResult?.getPhonicsLesson) {
      return;
    }

    setLiveLesson(liveLessonResult.getPhonicsLesson);
  }, [liveLessonResult]);

  useEffect(() => {
    if (
      !liveLessonOnboardingStatusResult?.getPhonicsLiveLessonOnboardingStatus
    ) {
      return;
    }

    setLiveLessonOnboardingStatus(
      liveLessonOnboardingStatusResult.getPhonicsLiveLessonOnboardingStatus,
    );
  }, [liveLessonOnboardingStatusResult]);

  useEffect(() => {
    if (!liveLessonMembershipResult?.createPhonicsLiveLessonMembership) {
      return;
    }

    setLiveLessonMembershipStatus(
      liveLessonMembershipResult.createPhonicsLiveLessonMembership,
    );
  }, [liveLessonMembershipResult]);

  useEffect(() => {
    if (
      !liveLessonMembershipStatusResult?.getPhonicsLiveLessonMembershipStatus
    ) {
      return;
    }

    setLiveLessonMembershipStatus(
      liveLessonMembershipStatusResult.getPhonicsLiveLessonMembershipStatus,
    );
  }, [liveLessonMembershipStatusResult]);

  useEffect(() => {
    if (!classStatus) {
      return;
    }
    if (!liveLesson) {
      return;
    }

    const liveLessonStatusUrl = `
      ${process.env.NEXT_PUBLIC_REST_API_URL}/phonics-sse/live-lesson-status/${classStatus.classId}/${liveLesson.id}
    `;

    const token = window.localStorage.getItem('accessToken');

    const liveLessonStatusEventSource = new EventSource(liveLessonStatusUrl, {
      fetch: (input, init) =>
        fetch(input, {
          ...init,
          headers: {
            ...init?.headers,
            source: 'phonics',
            authorization: token ? `Bearer ${token}` : '',
          },
        }),
    });

    liveLessonStatusEventSource.addEventListener(
      'update',
      handleLiveLessonStatusUpdate,
    );

    liveLessonStatusEventSource.onerror = () => {
      setLiveLessonStatusError(
        'There was am error while fetching the latest live lesson status.',
      );
    };

    return () => {
      liveLessonStatusEventSource.removeEventListener(
        'update',
        handleLiveLessonStatusUpdate,
      );
      liveLessonStatusEventSource.close();
    };
  }, [classStatus, liveLesson, token]);

  return (
    <LiveLessonContext.Provider
      value={{
        liveLesson,
        setLiveLesson,
        tryFetchLiveLessonStatus,
        liveLessonStatus,
        setLiveLessonStatus,
        liveLessonStatusError,
        beginLiveLesson,
        beginLiveLessonLoading: createPhonicsLiveLessonLoading,
        beginLiveLessonError: createPhonicsLiveLessonError?.message,
        endLiveLesson,
        requestLiveLessonOnboardingStatus,
        liveLessonOnboardingStatus,
        liveLessonOnboardingStatusLoading,
        liveLessonOnboardingStatusError:
          liveLessonOnboardingStatusError?.message,
        liveLessonLogin,
        liveLessonLoginLoading: phonicsLoginLoading,
        liveLessonLoginError: phonicsLoginError?.message,
        joinLiveLesson,
        tryFetchLiveLessonMembershipStatus,
        liveLessonMembershipStatus,
        liveLessonMembershipLoading,
        liveLessonMembershipError: liveLessonMembershipError?.message,
      }}
    >
      {children}
    </LiveLessonContext.Provider>
  );
};

export const useLiveLesson = () => useContext(LiveLessonContext);
